import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { LifeBuoy, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * Disaster-recovery panel: bulk-restores parent/customer accounts that were
 * deleted by past `parent_merge` audit logs. Use when a too-loose auto-merge
 * deleted accounts that weren't actually duplicates. Always preview first.
 */
export const BulkRestoreBanner = ({ onRestored }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [revertLinks, setRevertLinks] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.post('/admin/duplicates/parents/bulk-restore-merged-sources');
      setPreview(r.data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    let totalRestored = 0;
    const totalToRestore = preview?.restorable || 0;
    const t = toast.loading('Starting bulk restore…');
    try {
      // Loop with limit=25 to avoid the 60s gateway timeout. Stop when the
      // backend reports `restored: 0` in a batch (or restorable hit 0).
      const BATCH = 25;
      for (let i = 0; i < 200; i += 1) {
        const r = await api.post(
          `/admin/duplicates/parents/bulk-restore-merged-sources?apply=true&limit=${BATCH}&revert_links=${revertLinks}`
        );
        const restoredThisBatch = r.data?.restored ?? 0;
        const errorsThisBatch = (r.data?.errors || []).length;
        totalRestored += restoredThisBatch;
        toast.message(
          `Restoring… ${totalRestored}${totalToRestore ? ` of ${totalToRestore}` : ''}`,
          { id: t },
        );
        if (restoredThisBatch === 0 && errorsThisBatch === 0) break;
        if ((r.data?.restorable || 0) === 0) break;
      }
      toast.success(`Restored ${totalRestored} account(s).`, { id: t });
      setConfirmOpen(false);
      setOpen(false);
      setPreview(null);
      onRestored?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          || `Restore stopped after ${totalRestored} account(s)`,
        { id: t },
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <Card className="border-rose-200 bg-rose-50/40">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2">
                <LifeBuoy className="h-5 w-5 text-rose-700" />
              </div>
              <div>
                <p className="font-semibold text-rose-900">Disaster Recovery — Restore Merged Accounts</p>
                <p className="text-sm text-rose-800/80 max-w-3xl">
                  If a recent auto-merge deleted parent/customer accounts that weren't actually
                  duplicates, click <b>Preview Restorable</b> to see every account that can be
                  brought back from its merge audit snapshot. You'll review the list before
                  anything is written.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={busy}
              data-testid="bulk-restore-preview-btn"
            >
              {busy ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Scanning…</>
              ) : (
                <><LifeBuoy className="h-4 w-4 mr-2" />Preview Restorable</>
              )}
            </Button>
          </div>

          {preview && open && (
            <div className="mt-4 rounded-md border border-rose-200 bg-white p-3 space-y-3"
                 data-testid="bulk-restore-preview">
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <span><b>{preview.total_audits_considered}</b> merge audit(s)</span>
                <span>·</span>
                <span className="text-emerald-700"><b>{preview.restorable}</b> restorable</span>
                {preview.skipped_already_alive > 0 && (
                  <><span>·</span><span><b>{preview.skipped_already_alive}</b> already alive</span></>
                )}
                {preview.skipped_email_collision > 0 && (
                  <><span>·</span><span className="text-amber-700">
                    <b>{preview.skipped_email_collision}</b> email taken
                  </span></>
                )}
                {preview.skipped_no_snapshot > 0 && (
                  <><span>·</span><span className="text-slate-600">
                    <b>{preview.skipped_no_snapshot}</b> no snapshot
                  </span></>
                )}
              </div>

              {(preview.samples || []).length > 0 && (
                <div className="rounded-md border max-h-72 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Name</th>
                        <th className="text-left px-2 py-1">Email</th>
                        <th className="text-left px-2 py-1">Role</th>
                        <th className="text-left px-2 py-1">Merged Into</th>
                        <th className="text-left px-2 py-1">Merged At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.samples.map((s) => (
                        <tr key={s.audit_id} className="border-t">
                          <td className="px-2 py-1 font-medium">{s.source_name || '—'}</td>
                          <td className="px-2 py-1 text-slate-600">{s.source_email || '—'}</td>
                          <td className="px-2 py-1">{s.source_role}</td>
                          <td className="px-2 py-1 text-slate-500">{s.target_name || s.target_id?.slice(0, 8) + '…'}</td>
                          <td className="px-2 py-1 text-slate-500 font-mono">
                            {(s.merged_at || '').slice(0, 19).replace('T', ' ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.restorable > preview.samples.length && (
                    <div className="px-2 py-1 text-slate-500 text-center bg-slate-50">
                      +{preview.restorable - preview.samples.length} more not shown
                    </div>
                  )}
                </div>
              )}

              <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                <Checkbox
                  checked={revertLinks}
                  onCheckedChange={(v) => setRevertLinks(!!v)}
                  data-testid="bulk-restore-revert-links-cb"
                />
                <div>
                  <span className="font-medium">Also revert children, enrollments &amp; bookings</span>
                  <span className="block text-slate-500 text-xs">
                    Off by default. Leave unchecked to just restore the user records and decide
                    child/enrollment ownership manually afterwards.
                  </span>
                </div>
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={() => { setOpen(false); setPreview(null); }}>
                  Close
                </Button>
                <Button
                  className="bg-rose-600 hover:bg-rose-700"
                  disabled={preview.restorable === 0}
                  onClick={() => setConfirmOpen(true)}
                  data-testid="bulk-restore-apply-btn"
                >
                  Restore {preview.restorable} Account{preview.restorable === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Confirm bulk restore
            </AlertDialogTitle>
            <AlertDialogDescription>
              About to restore <b>{preview?.restorable}</b> deleted parent/customer account(s)
              from merge audit snapshots.
              {revertLinks
                ? ' Children, enrollments and bookings linked by these merges WILL be reverted to the restored sources.'
                : ' Children, enrollments and bookings will stay linked to the merge targets — only the user records are restored.'}
              {' '}This action is logged and cannot be undone in bulk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); apply(); }}
              disabled={applying}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="bulk-restore-confirm-btn"
            >
              {applying ? 'Restoring…' : `Yes, Restore ${preview?.restorable || 0}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
